import { SessionScope, type SessionStatus } from '@heidi/schema/ml-scribe';
import { createUserScopedStorageAtom } from '@heidi/utils/jotai-storage';

import { atom } from 'jotai';
import {
  SESSION_LIST_DEFAULT_SORT_BY,
  SESSION_LIST_DEFAULT_SORT_ORDER,
  SESSION_LIST_SCOPE_STORAGE_KEY,
} from '../../contracts';

/** Same shape as the design-system date range. `/data` cannot import that package. */
export type DateRange = { from?: Date; to?: Date };

export const isSessionsFiltersOpenAtom = atom(false);
/** Survives the remount that fires when the list switches between the
 *  DataTable branch and the empty-state branch. */
export const isSessionListFilterOpenAtom = atom(false);
// The date-range, status, and urgency filter atoms are cached per-scope for the
// new session list by `useNewSessionListViewPersistence`. The patient-profile
// filter is intentionally NOT cached. A new filter added here that should
// persist across scope/reload must also be wired into that hook.
export const selectedDateRangeFilterAtom = atom<DateRange>({
  from: undefined,
  to: undefined,
});
export const selectedPatientProfilesFilterAtom = atom<string[]>([]);
export const selectedStatusesFilterAtom = atom<SessionStatus[]>([]);
/**
 * "Urgent" filter. Urgency is a boolean flag on the session (see
 * `@/components/ui/session-urgency/types`) — when `true` the list sends
 * `is_urgent=true` server-side so only urgent sessions are returned (see
 * `docs/contracts/session-urgency.md`).
 */
export const isUrgentOnlyFilterAtom = atom<boolean>(false);

// The session list is current-team scoped, so the clinician filter's roster is
// current-team members — the correct option universe for `all` and `team`. On
// `direct_share` the listed sessions can be shared by clinicians outside the
// team, who never appear in the team roster, so the filter is hidden there (as
// it is for `me`) rather than offering a roster unrelated to the sessions shown.
export const isClinicianFilterApplicableForScope = (scope: SessionScope): boolean =>
  scope !== SessionScope.me && scope !== SessionScope.direct_share;

// The patient-profile filter's option roster is the current user's own patients
// (`useQueryPatientProfiles`), which only matches the sessions shown on `me`. On
// `all` and `direct_share` the listed sessions can belong to other clinicians'
// patients who never appear in that roster, so the filter is hidden there.
// Allow-list (unlike the clinician deny-list above) so an as-yet-unreachable
// scope like `team` — same roster mismatch — stays hidden by default.
export const isPatientProfileFilterApplicableForScope = (scope: SessionScope): boolean =>
  scope === SessionScope.me;

export type SortOption = 'createdAt' | 'updatedAt' | 'name' | 'status' | 'urgency';
export type SortOrder = 'asc' | 'desc';

// Single-declared in `@heidi/session/contracts`: these are elements 8 and 9 of
// the session-list cache key, so a consumer reproducing the default list key
// must read the same values without importing this app atom module.
export const DEFAULT_SORT_BY: SortOption = SESSION_LIST_DEFAULT_SORT_BY;
export const DEFAULT_SORT_ORDER: SortOrder = SESSION_LIST_DEFAULT_SORT_ORDER;

export const sessionsSortByAtom = atom<SortOption>(DEFAULT_SORT_BY);
export const sessionsSortOrderAtom = atom<SortOrder>(DEFAULT_SORT_ORDER);

/**
 * Active session-list scope tab, persisted per-user. Persisted in place (not a
 * new-list-only atom) because the shared filter hooks (`useSessionsFilters`,
 * `useClinicianFilter`, `useHasActiveFilters`) read it for both lists. The new
 * list drives it via its ScopeToggle; the legacy list has no scope UI and
 * resets it to the default on mount (see `SessionsFilters`), so a persisted
 * `all`/`direct_share` — e.g. left behind after a flag rollback — can't surface
 * clinician filtering in the legacy toolbar that its v3 query never honours. A
 * restored scope the user can no longer access is reset by
 * `useVisibleSessionScopes` on the new list.
 */
export const sessionListScopeAtom = createUserScopedStorageAtom<SessionScope>(
  SESSION_LIST_SCOPE_STORAGE_KEY,
  SessionScope.me
);

/** Scope-keyed clinician selection. Each scope keeps its own selection so
 *  switching tabs preserves per-scope state instead of leaking across. Also
 *  read/written externally by `useNewSessionListViewPersistence` (new-list
 *  cache) — keep its shape in sync with `selectedClinicianIdsFilterAtom`. */
export const selectedClinicianIdsByScopeAtom = atom<
  Partial<Record<SessionScope, string[]>>
>({});
export const selectedClinicianIdsFilterAtom = atom(
  (get) => get(selectedClinicianIdsByScopeAtom)[get(sessionListScopeAtom)] ?? [],
  (get, set, update: string[] | ((prev: string[]) => string[])) => {
    const scope = get(sessionListScopeAtom);
    const byScope = get(selectedClinicianIdsByScopeAtom);
    const prev = byScope[scope] ?? [];
    const next = typeof update === 'function' ? update(prev) : update;
    set(selectedClinicianIdsByScopeAtom, { ...byScope, [scope]: next });
  }
);

/** Updated on a 250ms debounce while typing, or immediately on Enter. */
export const sessionListSearchQueryAtom = atom<string>('');

/** Opaque signal — the value carries no meaning, only the change does. Bump
 *  via `set((prev) => prev + 1)` from a reset handler to ask the session list
 *  to scroll back to top. */
export const sessionsScrollToTopTriggerAtom = atom<number>(0);

export enum openedSessionTabEnum {
  UPCOMING = 'Schedule',
  PAST = 'Past',
}

export const openedSessionTabAtom = atom<openedSessionTabEnum>(openedSessionTabEnum.PAST);

export const sessionListSearchValueAtom = atom<string>('');
